// Which entrance animation a route mount plays.
//
// `PageTransition` (App.js) is keyed by a collapsed route key, so it only
// remounts on CROSS-SHELL navigation: login → /app, /app → /log-job/:id,
// /log-job/:id → /app, and the same for /jobs/:id. Tab switches inside a shell
// collapse to one key and never remount, so they never animate here.
//
// WHY THE RISE GOES AWAY ON THE WAY BACK (2026-09-04): `cvx-page-in` is a 7px
// translateY plus a fade. Every mount it fired on used to be covered — by the
// boot splash, or by ProtectedRoute rendering `null` through the whole 220ms
// while its MFA check ran. Removing that blank frame (see mfaGateCache.js) made
// the rise visible for the first time, and the founder read the route board
// sliding up on arrival as a glitch. It is: returning to the board is not an
// opening, and the app's own rule is motion for opening overlays and detail
// pages, none for moving between peers. So:
//
//   • first mount after a page load → fade only, 280ms. Unchanged. Content must
//     not slide up THROUGH the splash as it fades out (that read as a flicker —
//     the reason `cvx-page-in-fade` exists at all).
//   • returning to a SHELL (/app, /platform) → fade only. Nothing travels.
//   • opening a LEAF over the shell (/log-job/:id, /jobs/:id) → keep the rise.
//     That is a genuine opening, which is what the motion is for.

// ACCEPTED GAP, not an oversight: signing out navigates to '/' from a shell
// key, and '/' is a leaf here, so logout plays the rise. Leaving the app is not
// the "returning to where you already were" case this rule is about, and the
// login screen runs its own entrance on top. Revisit only if it reads wrong.

const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';

/** Collapsed transition keys that represent a persistent SHELL, not a leaf page. */
export const SHELL_TRANSITION_KEYS = Object.freeze(['app', 'platform']);

/**
 * @param {{ transitionKey: string|null|undefined, isFirstPageIn: boolean, reduced?: boolean, still?: boolean }} args
 *   `reduced` = the viewer asked for less motion (prefers-reduced-motion). This
 *   animation is an INLINE style, so index.css's reduced-motion media blocks
 *   never reach it — the gate has to live here.
 *   `still` (KAN-765) = the page brings its own entrance (Log Job entered from
 *   the unscheduled-visit flow pushes its BODY in under a header that must not
 *   move), so the whole-page rise/fade is skipped.
 * @returns {string} a CSS `animation` shorthand
 */
export function pageEntranceAnimation({ transitionKey, isFirstPageIn, reduced = false, still = false }) {
  if (reduced || still) return 'none';
  if (isFirstPageIn) return `cvx-page-in-fade 280ms ${EASE} forwards`;
  if (SHELL_TRANSITION_KEYS.includes(transitionKey)) return `cvx-page-in-fade 220ms ${EASE} forwards`;
  return `cvx-page-in 220ms ${EASE} forwards`;
}

export default pageEntranceAnimation;
